import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack, Tabs, Tab
} from '@mui/material';
import { useSnackbar } from 'notistack';
import movMonedaService from '../../services/movMonedaService';
import TipoMonedaSearch from '../../components/TipoMonedaSearch';

interface MovMonedaFormData {
  codigoMoneda: number;
  fechaCotizacion: string;
  cotizacion: number;
  nombreMoneda?: string;
}

const defaultForm: MovMonedaFormData = {
  codigoMoneda: 0,
  fechaCotizacion: new Date().toISOString().split('T')[0],
  cotizacion: 0
};

const MovMonedaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoMoneda, fechaCotizacion } = useParams();
  const [formData, setFormData] = useState<MovMonedaFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [monedaSearchOpen, setMonedaSearchOpen] = useState(false);

  const loadMovMoneda = useCallback(async () => {
    if (!codigoMoneda || !fechaCotizacion) return;
    try {
      setLoading(true);
      const movMoneda = await movMonedaService.getMovMoneda(Number(codigoMoneda), decodeURIComponent(fechaCotizacion));
      setFormData({
        codigoMoneda: movMoneda.codigoMoneda,
        fechaCotizacion: movMoneda.fechaCotizacion,
        cotizacion: movMoneda.cotizacion,
        nombreMoneda: movMoneda.nombreMoneda
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar la cotización', { variant: 'error' });
      navigate('/sistema/mov-moneda');
    } finally {
      setLoading(false);
    }
  }, [codigoMoneda, fechaCotizacion, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoMoneda && fechaCotizacion) loadMovMoneda();
  }, [codigoMoneda, fechaCotizacion, loadMovMoneda]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoMoneda' || name === 'cotizacion') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleMonedaSelect = (moneda: any) => {
    setFormData(prev => ({
      ...prev,
      codigoMoneda: moneda.codigoMoneda,
      nombreMoneda: moneda.nombreMoneda
    }));
    setMonedaSearchOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.codigoMoneda) {
        enqueueSnackbar('Debe seleccionar una moneda', { variant: 'warning' });
        return;
      }

      if (!formData.cotizacion || formData.cotizacion <= 0) {
        enqueueSnackbar('La cotización debe ser mayor a 0', { variant: 'warning' });
        return;
      }

      const fechaActual = new Date();
      const fechaSeleccionada = new Date(formData.fechaCotizacion);
      if (fechaSeleccionada > fechaActual) {
        enqueueSnackbar('La fecha no puede ser futura', { variant: 'warning' });
        return;
      }

      if (codigoMoneda && fechaCotizacion) {
        await movMonedaService.updateMovMoneda(Number(codigoMoneda), decodeURIComponent(fechaCotizacion), formData);
      } else {
        await movMonedaService.createMovMoneda(formData);
      }

      enqueueSnackbar('Cotización guardada exitosamente', { variant: 'success' });
      navigate('/sistema/mov-moneda');
    } catch (error: any) {
      console.error('Error al guardar cotización:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la cotización', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            {codigoMoneda && fechaCotizacion ? 'Editar Cotización' : 'Nueva Cotización'}
          </Typography>

          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 3 }}>
            <Tab label="General" />
            <Tab label="Cotización" />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {currentTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1}>
                    <TextField 
                      label="Moneda" 
                      value={formData.nombreMoneda || formData.codigoMoneda || ''} 
                      fullWidth 
                      required
                      disabled
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setMonedaSearchOpen(true)}
                      disabled={!!(codigoMoneda && fechaCotizacion)}
                      sx={{ minWidth: 120 }}
                    >
                      Buscar
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="fechaCotizacion" 
                    label="Fecha de Cotización" 
                    type="date"
                    value={formData.fechaCotizacion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    disabled={!!(codigoMoneda && fechaCotizacion)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            {currentTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="cotizacion" 
                    label="Cotización" 
                    type="number"
                    value={formData.cotizacion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    inputProps={{ step: '0.01', min: '0' }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/mov-moneda')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <TipoMonedaSearch
        open={monedaSearchOpen}
        onClose={() => setMonedaSearchOpen(false)}
        onSelect={handleMonedaSelect}
      />
    </Box>
  );
};

export default MovMonedaForm;
